import { Component, Input, OnInit } from '@angular/core';
import { IVTableColumn } from '@app/directives/_layout/v-table/_models/IVTableColumn';
import { VTableService } from '@app/directives/_layout/v-table/_services/v-table.service';
import { SortDirection } from '@app/enum/SortDirection';
import { SortType } from '@app/enum/SortType';
import { IAccountUnitMappingExtended } from '@app/interfaces/IAccountUnitMappingExtended';
import { IVehicleUnitMappingExtended } from '@app/interfaces/IVehicleUnitMappingExtended';
import { IConfigMappings, IFirmwareMappings, UnitConfigAndFirmwareStatus } from '@app/main/modules/foundation/_services/peripheral.service.ajs';
import { Observable } from 'rxjs';
import { HardwareMappingService } from '../../../_services/hardware-mapping.service';
import './unit-overview.component.scss';

@Component({
    selector: 'unit-overview',
    template: require('./unit-overview.component.html')
})
export class UnitOverviewComponent implements OnInit {
    @Input() serial: string;

    accountMapping$: Observable<IAccountUnitMappingExtended[]>
    configData$: Observable<IConfigMappings[]>;
    firmwareData$: Observable<IFirmwareMappings[]>;
    vehicleData$: Observable<IVehicleUnitMappingExtended[]>;

    accountTableColumns: IVTableColumn<IAccountUnitMappingExtended>[];
    configTableColumns: IVTableColumn<IConfigMappings>[];
    firmwareTableColumns: IVTableColumn<IFirmwareMappings>[];
    vehicleTableColumns: IVTableColumn<IVehicleUnitMappingExtended>[];

    loading: boolean;

    constructor(
        private readonly hardwareMappingService: HardwareMappingService,
        private readonly vTableService: VTableService
    ) { }

    ngOnInit(): void {
        this.loadData();
    }

    //--

    private loadData(): void {
        this.loading = true;
        this.createTables();

        this.accountMapping$ = this.hardwareMappingService.getAccountMappings(this.serial);
        this.configData$ = this.hardwareMappingService.getConfigMappings(this.serial);
        this.firmwareData$ = this.hardwareMappingService.getFirmwareMappings(this.serial);
        this.vehicleData$ = this.hardwareMappingService.getVehicleMappings(this.serial);
    }

    private createTables(): void {

        this.configTableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Config", width: 180 }),
                createCellFunction: config => this.vTableService.createTableCell(`${config.Identifier} - ${config.Description}`)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status", width: 100 }),
                createCellFunction: config => this.vTableService.createEnumCell(UnitConfigAndFirmwareStatus, "UnitConfigAndFirmwareStatus", config.UnitConfigStatus)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Start", width: 200, sorted: true, sortType: SortType.Date, sortDirection: SortDirection.Descending }),
                createCellFunction: config => {
                    const formattedDate = this.hardwareMappingService.getDateDescription(config.StartUtc);
                    return this.vTableService.createTableCell(formattedDate, { sortData: new Date(config.StartUtc) });
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.End", width: 200 }),
                createCellFunction: config => {
                    const formattedDate = this.hardwareMappingService.getDateDescription(config.EndUtc);
                    return this.vTableService.createTableCell(formattedDate);
                }
            }
        ];

        this.firmwareTableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Firmware", width: 180 }),
                createCellFunction: config => this.vTableService.createTableCell(config.Description)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status", width: 100 }),
                createCellFunction: config => this.vTableService.createEnumCell(UnitConfigAndFirmwareStatus, "UnitConfigAndFirmwareStatus", config.UnitFirmwareStatus)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Start", width: 200, sorted: true, sortType: SortType.Date, sortDirection: SortDirection.Descending }),
                createCellFunction: config => {
                    const formattedDate = this.hardwareMappingService.getDateDescription(config.StartUtc);
                    return this.vTableService.createTableCell(formattedDate, { sortData: new Date(config.StartUtc) });
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.End", width: 200 }),
                createCellFunction: config => {
                    const formattedDate = this.hardwareMappingService.getDateDescription(config.EndUtc);
                    return this.vTableService.createTableCell(formattedDate);
                }
            }
        ];

        this.vehicleTableColumns = [
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: vehicle => this.vTableService.createVehicleRegCell(vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Start", width: 200, sorted: true, sortType: SortType.Date, sortDirection: SortDirection.Descending }),
                createCellFunction: vehicle => {
                    const formattedDate = this.hardwareMappingService.getDateDescription(vehicle.StartUtc);
                    return this.vTableService.createTableCell(formattedDate, { sortData: new Date(vehicle.StartUtc) });
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.End", width: 200 }),
                createCellFunction: vehicle => {
                    const formattedDate = this.hardwareMappingService.getDateDescription(vehicle.EndUtc);
                    return this.vTableService.createTableCell(formattedDate);
                }
            }
        ];

        this.accountTableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Company", width: 180 }),
                createCellFunction: account => this.vTableService.createTableCell(account.CompanyName)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Start", width: 200, sorted: true, sortType: SortType.Date, sortDirection: SortDirection.Descending }),
                createCellFunction: account => {
                    const formattedDate = this.hardwareMappingService.getDateDescription(account.StartUtc);
                    return this.vTableService.createTableCell(formattedDate, { sortData: new Date(account.StartUtc) });
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.End", width: 200 }),
                createCellFunction: account => {
                    const formattedDate = this.hardwareMappingService.getDateDescription(account.EndUtc);
                    return this.vTableService.createTableCell(formattedDate);
                }
            }
        ];
    }

}
